import { RefObject, useEffect, useState } from 'react';

export const TIMELINE_CARD_ATTRIBUTE = 'data-timeline-card';

const DEFAULT_FONT_SIZE = 16;
/** Rough card height in `em` before a real card has been measured (icon row + padding). */
const FALLBACK_CARD_EM = 2.6;
const MIN_PAGE_SIZE = 1;

interface Options {
  /** A single column's card area; its height is the vertical budget for one page of cards. */
  heightRef: RefObject<HTMLElement>;
  /** Root of the board; cards anywhere underneath are measured so the tallest wins. */
  cardsRootRef: RefObject<HTMLElement>;
  /** Vertical gap between cards in px (must match the CSS used to lay them out). */
  gapPx: number;
  /** Any value whose change should trigger a re-measure (e.g. patient count, font tier). */
  deps?: unknown[];
}

/**
 * Computes how many patient cards fit vertically inside a phase column.
 *
 * Mirrors the approach in `usePanelDistribution`: observe the real container height, measure a
 * card that is actually in the DOM, and derive the page size from that rather than guessing from
 * the viewport. One value is shared across all columns so their pages stay visually aligned.
 */
export const useTimelinePageSize = ({ heightRef, cardsRootRef, gapPx, deps = [] }: Options): number => {
  const [containerHeight, setContainerHeight] = useState(0);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);

  useEffect(() => {
    const heightEl = heightRef.current;
    const cardsRoot = cardsRootRef.current;
    if (!heightEl || !cardsRoot) return undefined;

    const measure = () => {
      const rect = heightEl.getBoundingClientRect();
      if (Number.isFinite(rect.height) && rect.height > 0) {
        setContainerHeight(rect.height);
      }

      const computedFont = parseFloat(window.getComputedStyle(cardsRoot).fontSize);
      if (Number.isFinite(computedFont) && computedFont > 0) {
        setFontSize(computedFont);
      }

      // Use the tallest rendered card so a column with an extra translation line
      // never overflows its page.
      const cards = cardsRoot.querySelectorAll<HTMLElement>(`[${TIMELINE_CARD_ATTRIBUTE}="true"]`);
      let tallest = 0;
      cards.forEach(card => {
        const h = card.getBoundingClientRect().height;
        if (Number.isFinite(h) && h > tallest) tallest = h;
      });
      if (tallest > 0) {
        setCardHeight(tallest);
      }
    };

    measure();

    const observer = new ResizeObserver(() => measure());
    observer.observe(heightEl);
    observer.observe(cardsRoot);

    // Cards animate in, so measure again once the first paint has settled.
    const settle = window.setTimeout(measure, 600);

    return () => {
      observer.disconnect();
      window.clearTimeout(settle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightRef, cardsRootRef, ...deps]);

  if (containerHeight <= 0) {
    return MIN_PAGE_SIZE;
  }

  const effectiveCardHeight = cardHeight ?? fontSize * FALLBACK_CARD_EM;
  const stride = effectiveCardHeight + gapPx;
  const fit = Math.floor((containerHeight + gapPx) / stride);

  return Math.max(MIN_PAGE_SIZE, Number.isFinite(fit) ? fit : MIN_PAGE_SIZE);
};
